<template>
  <v-app>
    <vue-header></vue-header>
    <v-main :style="$vuetify.breakpoint.xs ? 'padding:0px!important;margin-top: 100px;' : 'padding:0px!important;margin-top: 140px;'">
      <v-container fluid style="margin-bottom:150px;" :class="this.$vuetify.breakpoint.xs ? 'px-0 py-0' : 'px-6 py-6'">
        <v-row style="margin:0px!important;">
          <v-col cols="12">
            <!-- Page Header -->
            <div class="d-flex align-center mb-6">
              <v-icon size="48" color="orange" class="mr-4">mdi-robot</v-icon>
              <div>
                <h1 class="text-h4 font-weight-bold text-typo">AI Digital Twin Settings</h1>
                <p class="text-subtitle-1 text--secondary mb-0">Manage your personalized AI assistant</p>
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card class="card-shadow border-radius-xl overflow-hidden">
              <!-- Tabs Navigation -->
              <v-tabs v-model="activeTab" background-color="transparent" color="orange" class="px-6 pt-4">
                <v-tab class="font-weight-bold">
                  <v-icon left class="material-icons-round">smart_toy</v-icon>
                  AI Personality
                </v-tab>
                <v-tab class="font-weight-bold">
                  <v-icon left class="material-icons-round">mic</v-icon>
                  Voice Agent
                </v-tab>
                <v-tab class="font-weight-bold">
                  <v-icon left class="material-icons-round">phone</v-icon>
                  Conversations
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="activeTab">
                <!-- AI Personality Tab -->
                <v-tab-item>
                  <v-card-text class="pa-6">
                <!-- Loading State -->
                <div v-if="loading" class="text-center py-8">
                  <v-progress-circular indeterminate color="orange" size="64"></v-progress-circular>
                  <div class="mt-4 text-h6">Loading your AI settings...</div>
                </div>

                <!-- Not Completed State -->
                <div v-else-if="!personalityData.completed" class="text-center py-8">
                  <v-icon size="80" color="orange" class="mb-4">mdi-robot-outline</v-icon>
                  <h2 class="text-h4 font-weight-bold mb-4 text-typo">Set Up Your AI Digital Twin</h2>
                  <p class="text-h6 mb-6 text--secondary" style="max-width: 600px; margin: 0 auto;">
                    Create a personalized AI assistant that talks exactly like you to help customers with vehicle information and applications.
                  </p>
                  <v-btn
                    color="orange"
                    large
                    @click="startOnboarding"
                    class="font-weight-bold px-8 py-3"
                    elevation="4"
                  >
                    <v-icon left>mdi-play</v-icon>
                    Start Personality Setup
                  </v-btn>
                </div>

                <!-- Completed State -->
                <div v-else>
                  <v-row>
                    <!-- Personality Summary -->
                    <v-col cols="12" md="8">
                      <h3 class="text-h5 font-weight-bold mb-4 text-typo">Your AI Personality</h3>
                      <v-card class="card-shadow border-radius-lg mb-4" elevation="0">
                        <v-card-text class="pa-4">
                          <div v-html="formatText(personalityData.summary)" class="text-body-1 line-height-lg"></div>
                        </v-card-text>
                        <v-card-actions class="pa-4 pt-0">
                          <v-btn
                            text
                            color="orange"
                            @click="editPersonality"
                            class="font-weight-bold"
                          >
                            <v-icon left class="material-icons-round">edit</v-icon>
                            Edit Personality
                          </v-btn>

                          <!-- Approve Button (only show if not approved) -->
                          <v-btn
                            v-if="!personalityData.approved"
                            color="green"
                            dark
                            @click="approvePersonality"
                            :loading="approving"
                            class="font-weight-bold ml-2"
                          >
                            <v-icon left class="material-icons-round">check</v-icon>
                            Approve
                          </v-btn>

                          <v-spacer></v-spacer>
                          <v-chip
                            :color="personalityData.approved ? 'green' : 'orange'"
                            :text-color="personalityData.approved ? 'white' : 'white'"
                            class="font-weight-bold"
                          >
                            <v-icon left small>
                              {{ personalityData.approved ? 'mdi-check-circle' : 'mdi-clock' }}
                            </v-icon>
                            {{ personalityData.approved ? 'Active' : 'Pending Approval' }}
                          </v-chip>
                        </v-card-actions>
                      </v-card>

                      <!-- Agent Link -->
                      <h3 class="text-h5 font-weight-bold mb-4 text-typo">Your AI Assistant Link</h3>
                      <v-card class="card-shadow border-radius-lg mb-4" elevation="0">
                        <v-card-text class="pa-4">
                          <div class="d-flex align-center">
                            <v-text-field
                              :value="personalityData.agent_link"
                              readonly
                              outlined
                              dense
                              hide-details
                              class="mr-3"
                              prepend-inner-icon="fa-link"
                            ></v-text-field>
                            <v-btn
                              color="orange"
                              @click="copyLink"
                              dark
                              class="font-weight-bold"
                              elevation="2"
                            >
                              <v-icon class="material-icons-round">content_copy</v-icon>
                            </v-btn>
                          </div>
                          <div class="mt-3 text--secondary">
                            Share this link with customers to chat with your AI assistant
                          </div>
                        </v-card-text>
                      </v-card>

                      <!-- Actions -->
                      <div class="d-flex flex-wrap gap-3">
                        <v-btn
                          color="orange"
                          dark
                          @click="testAssistant"
                          class="font-weight-bold"
                          elevation="2"
                        >
                          <v-icon left class="material-icons-round">chat</v-icon>
                          Test Assistant
                        </v-btn>
                        <v-btn
                          outlined
                          dark
                          color="orange"
                          @click="resetPersonality"
                          :loading="resetting"
                          class="font-weight-bold"
                        >
                          <v-icon left class="material-icons-round">refresh</v-icon>
                          Redo Setup
                        </v-btn>
                      </div>
                    </v-col>

                    <!-- Quick Stats -->
                    <v-col cols="12" md="4">
                      <h3 class="text-h5 font-weight-bold mb-4 text-typo">Quick Stats</h3>
                      <v-card class="card-shadow border-radius-lg" elevation="0">
                        <v-list class="pa-2">
                          <v-list-item class="px-4 py-3">
                            <v-list-item-icon class="mr-4">
                              <v-icon color="green" size="24" class="material-icons-round">check_circle</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold">Setup Complete</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>

                          <v-divider class="mx-4"></v-divider>

                          <v-list-item class="px-4 py-3">
                            <v-list-item-icon class="mr-4">
                              <v-icon class="material-icons-round" :color="personalityData.approved ? 'green' : 'orange'" size="24">
                                {{ personalityData.approved ? 'check_circle' : 'clock' }}
                              </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold">
                                {{ personalityData.approved ? 'AI Active' : 'Awaiting Approval' }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>

                          <v-divider class="mx-4"></v-divider>

                          <v-list-item class="px-4 py-3">
                            <v-list-item-icon class="mr-4">
                              <v-icon color="green" size="24" class="material-icons-round">link</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold">Unique Link Ready</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
                  </v-card-text>
                </v-tab-item>

                <!-- Voice Agent Tab -->
                <v-tab-item>
                  <v-card-text class="pa-6">
                    <!-- Voice Agent Content -->
                    <div v-if="voiceAgentLoading" class="text-center py-8">
                      <v-progress-circular indeterminate color="orange" size="64"></v-progress-circular>
                      <div class="mt-4 text-h6">Loading voice agent settings...</div>
                    </div>

                    <div v-else-if="!voiceAgent.has_voice_agent" class="text-center py-8">
                      <v-icon size="80" color="grey" class="mb-4 material-icons-round">mic_off</v-icon>
                      <h2 class="text-h4 font-weight-bold mb-4 text-typo">No Voice Agent Configured</h2>
                      <p class="text-h6 mb-6 text--secondary" style="max-width: 600px; margin: 0 auto;">
                        Contact your admin to set up a voice agent for your account.
                      </p>
                    </div>

                    <div v-else>
                      <v-row>
                        <!-- Voice Agent Status & Phone Number -->
                        <v-col cols="12" md="6">
                          <v-card outlined class="pa-4 h-100">
                            <div class="text-center">

                              <v-chip
                                :color="voiceAgent.voice_agent_enabled ? 'green' : 'red'"
                                class="mb-3"
                                small
                                dark
                              >
                                {{ voiceAgent.voice_agent_enabled ? 'Active' : 'Inactive' }}
                              </v-chip>

                              <div v-if="voiceAgent.retell_phone_number" class="mb-3">
                                <h4 class="text-h6 font-weight-bold mb-2">Your Voice Agent Number</h4>
                                <div class="d-flex align-center justify-center">
                                  <span class="text-h5 font-weight-bold text-primary mr-2">
                                    {{ voiceAgent.retell_phone_number }}
                                  </span>
                                  <v-btn
                                    icon
                                    small
                                    @click="copyPhoneNumber"
                                    :color="phoneNumberCopied ? 'green' : 'blue'"
                                  >
                                    <v-icon class="material-icons-round">{{ phoneNumberCopied ? 'check' : 'content_copy' }}</v-icon>
                                  </v-btn>
                                </div>
                                <p class="text-caption mt-2">Share this number with your customers</p>
                              </div>

                              <div v-else>
                                <p class="text-body-2">No phone number assigned yet</p>
                              </div>
                            </div>
                          </v-card>
                        </v-col>

                        <!-- Voice Agent Prompt Editor -->
                        <v-col cols="12" md="6">
                          <v-card outlined class="pa-4 h-100">
                            <h5 class="text-h6 font-weight-bold mb-3">
                              <v-icon class="mr-2 material-icons-round">description</v-icon>
                              Agent Instructions
                            </h5>

                            <v-textarea
                              v-model="voiceAgentPrompt"
                              label="Voice Agent Instructions"
                              rows="6"
                              outlined
                              dense
                              class="font-size-input"
                              placeholder="Enter instructions for your voice agent..."
                              :disabled="promptLoading"
                            ></v-textarea>

                            <div class="d-flex justify-space-between align-center">
                              <v-btn
                                color="orange"
                                @click="updateVoiceAgentPrompt"
                                :loading="promptLoading"
                                :disabled="!voiceAgentPrompt || voiceAgentPrompt === voiceAgent.voice_agent_prompt"
                              >
                                <v-icon left class="material-icons-round">save</v-icon>
                                Update Instructions
                              </v-btn>

                              <v-btn
                                color="secondary"
                                outlined
                                @click="resetPrompt"
                                :disabled="promptLoading"
                              >
                                <v-icon left class="material-icons-round">refresh</v-icon>
                                Reset to Default
                              </v-btn>
                            </div>
                          </v-card>
                        </v-col>
                      </v-row>

                      <!-- Test Voice Agent Section -->
                      <v-row class="mt-4">
                        <v-col cols="12">
                          <v-card outlined class="pa-4">
                            <h5 class="text-h6 font-weight-bold mb-3">
                              <v-icon class="mr-2 material-icons-round">phone</v-icon>
                              Test Voice Agent
                            </h5>

                            <div class="text-center">
                              <p class="text-body-2 mb-4">
                                Test your voice agent directly in your browser. Click the button below to start a web call with your AI assistant.
                              </p>

                              <div v-if="!isWebCallActive" class="mb-4">
                                <v-btn
                                  color="green"
                                  large
                                  dark
                                  @click="startWebCall"
                                  :loading="webCallLoading"
                                  :disabled="!voiceAgent.has_voice_agent || !voiceAgent.voice_agent_enabled"
                                  class="px-6 py-3"
                                  style="border-radius: 25px; font-weight: bold; text-transform: none;"
                                >
                                  <v-icon left class="material-icons-round">call</v-icon>
                                  Start Voice Test
                                </v-btn>
                              </div>

                              <div v-else class="mb-4">
                                <div class="mb-3">
                                  <v-chip color="green" text-color="white" class="mb-2">
                                    <v-icon left small class="material-icons-round">phone_in_talk</v-icon>
                                    Call Active
                                  </v-chip>
                                  <br>
                                  <v-chip
                                    :color="isAgentSpeaking ? 'orange' : 'blue'"
                                    text-color="white"
                                    class="mb-2"
                                  >
                                    <v-icon left small class="material-icons-round">
                                      {{ isAgentSpeaking ? 'record_voice_over' : 'mic' }}
                                    </v-icon>
                                    {{ isAgentSpeaking ? 'Agent Speaking' : 'Listening' }}
                                  </v-chip>
                                </div>

                                <v-btn
                                  color="red"
                                  large
                                  dark
                                  @click="stopWebCall"
                                  class="px-6 py-3"
                                  style="border-radius: 25px; font-weight: bold; text-transform: none;"
                                >
                                  <v-icon left class="material-icons-round">call_end</v-icon>
                                  End Call
                                </v-btn>
                              </div>

                              <!-- Call Transcript -->
                              <div v-if="webCallTranscript.length > 0" class="mt-4">
                                <h6 class="text-subtitle-1 font-weight-bold mb-2">Live Transcript</h6>
                                <v-card outlined class="pa-3" style="max-height: 200px; overflow-y: auto;">
                                  <div v-for="(message, index) in webCallTranscript" :key="index" class="mb-2">
                                    <div class="d-flex align-start">
                                      <v-chip
                                        :color="message.role === 'agent' ? 'orange' : 'blue'"
                                        text-color="white"
                                        small
                                        class="mr-2"
                                      >
                                        {{ message.role === 'agent' ? 'AI' : 'You' }}
                                      </v-chip>
                                      <span class="text-body-2">{{ message.content }}</span>
                                    </div>
                                  </div>
                                </v-card>
                              </div>

                              <p class="text-caption mt-3" v-if="!voiceAgent.has_voice_agent || !voiceAgent.voice_agent_enabled">
                                Voice agent must be enabled to test web calls.
                              </p>
                            </div>
                          </v-card>
                        </v-col>
                      </v-row>

                      <!-- Natural Laughter Enhancement Section -->
                      <v-row class="mt-4">
                        <v-col cols="12">
                          <v-card outlined class="pa-4" style="border: 2px solid #ff9800;">
                            <h5 class="text-h6 font-weight-bold mb-3 orange--text">
                              <v-icon class="mr-2 material-icons-round" color="orange">sentiment_very_satisfied</v-icon>
                              Natural Laughter Enhancement
                            </h5>

                            <div class="text-center">
                              <p class="text-body-2 mb-4">
                                Upgrade your voice agent with natural laughter capabilities! Your AI will now laugh naturally when users say something funny,
                                making conversations more engaging and human-like.
                              </p>

                              <v-alert
                                type="info"
                                outlined
                                dense
                                class="mb-4"
                              >
                                <div class="text-body-2">
                                  <strong>New Features:</strong>
                                  <ul class="mt-2" style="text-align: left;">
                                    <li>Natural laughter generated with your cloned voice</li>
                                    <li>Emotion detection for appropriate humor responses</li>
                                    <li>Enhanced conversation flow and engagement</li>
                                    <li>Professional yet personable interactions</li>
                                  </ul>
                                </div>
                              </v-alert>

                              <!-- Show upgrade button if laughter not enabled -->
                              <div v-if="!isLaughterEnhancementEnabled">
                                <v-btn
                                  color="orange"
                                  large
                                  dark
                                  @click="updateAgentWithLaughter"
                                  :loading="laughterUpdateLoading"
                                  :disabled="!voiceAgent.has_voice_agent || !voiceAgent.voice_agent_enabled"
                                  class="px-6 py-3"
                                  style="border-radius: 25px; font-weight: bold; text-transform: none;"
                                >
                                  <v-icon left class="material-icons-round">upgrade</v-icon>
                                  Enable Natural Laughter
                                </v-btn>

                                <p class="text-caption mt-3" v-if="!voiceAgent.has_voice_agent || !voiceAgent.voice_agent_enabled">
                                  Voice agent must be created and enabled to add laughter capabilities.
                                </p>
                              </div>

                              <!-- Show success message if laughter already enabled -->
                              <div v-else-if="isLaughterEnhancementEnabled">
                                <v-alert
                                  type="success"
                                  outlined
                                  dense
                                  class="mb-3"
                                >
                                  <div class="d-flex align-center">
                                    <div>
                                      <strong>Natural Laughter Enabled!</strong>
                                      <div class="text-caption">
                                        Updated {{ formatDate(voiceAgent.laughter_enhancement_updated_at) }}
                                      </div>
                                    </div>
                                  </div>
                                </v-alert>

                                <p class="text-body-2 text-center">
                                  🎭 Your voice agent now laughs naturally during conversations!
                                  Try making some jokes during voice chats to see it in action.
                                </p>
                              </div>

                              <!-- Fallback loading state -->
                              <div v-else-if="laughterUpdateLoading">
                                <v-progress-circular indeterminate color="orange" class="mb-3"></v-progress-circular>
                                <p class="text-body-2 text-center">Updating voice agent with laughter capabilities...</p>
                              </div>
                            </div>
                          </v-card>
                        </v-col>
                      </v-row>

                      <!-- Voice Customization Section -->
                      <v-row class="mt-4">
                        <v-col cols="12">
                          <v-card outlined class="pa-4">
                            <h5 class="text-h6 font-weight-bold mb-3">
                              <v-icon class="mr-2 material-icons-round">mic</v-icon>
                              Voice Customization
                            </h5>

                            <v-tabs v-model="voiceTab" background-color="transparent" color="orange">
                              <v-tab>
                                <v-icon left class="material-icons-round">upload</v-icon>
                                Upload Voice
                              </v-tab>
                              <v-tab>
                                <v-icon left class="material-icons-round">mic</v-icon>
                                Record Voice
                              </v-tab>
                              <v-tab v-if="currentVoiceFile">
                                <v-icon left class="material-icons-round">audiotrack</v-icon>
                                Current Voice
                              </v-tab>
                            </v-tabs>

                            <v-tabs-items v-model="voiceTab">
                              <v-tab-item>
                                <div class="pa-4">
                                  <v-file-input
                                    v-model="voiceFile"
                                    label="Upload voice sample (MP3, WAV, M4A)"
                                    accept="audio/*"
                                    prepend-icon="mic"
                                    outlined
                                    dense
                                    show-size
                                    :loading="voiceUploadLoading"
                                    @change="handleVoiceFileUpload"
                                  ></v-file-input>

                                  <div v-if="uploadedVoiceUrl" class="mt-3">
                                    <p class="text-body-2 mb-2">Preview uploaded voice:</p>
                                    <audio controls :src="uploadedVoiceUrl" class="mb-3" style="width: 100%;"></audio>
                                  </div>

                                  <p class="text-caption mt-2 mb-3">
                                    Upload a clear voice sample (10-60 seconds). Admin will process your voice for the agent.
                                    Best results with high-quality audio in a quiet environment.
                                  </p>

                                  <v-btn
                                    color="orange"
                                    @click="uploadVoiceToRetell"
                                    :loading="voiceUploadLoading"
                                    :disabled="!voiceFile"
                                  >
                                    <v-icon left class="material-icons-round">upload</v-icon>
                                    Upload Voice
                                  </v-btn>
                                </div>
                              </v-tab-item>

                              <v-tab-item>
                                <div class="pa-4 text-center">
                                  <!-- Microphone Test Button -->
                                  <div class="mb-4" v-if="!isRecording && !recordedAudio">
                                    <v-btn
                                      color="info"
                                      outlined
                                      small
                                      @click="testMicrophone"
                                      :loading="micTestLoading"
                                      class="mb-3"
                                    >
                                      Test Microphone
                                    </v-btn>
                                    <br>
                                    <span class="text-caption">Test your microphone before recording</span>
                                  </div>

                                  <div class="mb-4">
                                    <v-btn
                                      :color="isRecording ? 'red' : 'orange'"
                                      large
                                      dark
                                      @click="toggleRecording"
                                      :disabled="voiceRecordLoading"
                                      class="px-6 py-3"
                                      style="border-radius: 25px; font-weight: bold;text-transform:none;"
                                    >
                                      <v-icon left class="material-icons-round">{{ isRecording ? 'stop' : 'mic' }}</v-icon>
                                      {{ isRecording ? 'Stop' : 'Twin Talk™' }}
                                    </v-btn>
                                  </div>

                                  <p class="text-body-2 mb-2">
                                    {{ isRecording ? `Recording... ${recordingDuration}s` : 'Click Twin Talk™ to start recording' }}
                                  </p>
                                  <p class="text-caption mb-3">
                                    Record a clear voice sample (10-60 seconds). Admin will process your voice for the agent.
                                  </p>

                                  <div v-if="recordedAudio" class="mt-4">
                                    <p class="text-body-2 mb-2">Preview recorded voice:</p>
                                    <audio controls :src="recordedAudio" class="mb-3" style="width: 100%;"></audio>
                                    <br>
                                    <v-btn
                                      color="orange"
                                      @click="uploadRecordedVoiceToRetell"
                                      :loading="voiceRecordLoading"
                                      class="mr-2"
                                    >
                                      <v-icon left class="material-icons-round">check</v-icon>
                                      Apply Voice
                                    </v-btn>
                                    <v-btn
                                      outlined
                                      @click="clearRecording"
                                      :disabled="voiceRecordLoading"
                                    >
                                      <v-icon left class="material-icons-round">delete</v-icon>
                                      Clear Recording
                                    </v-btn>
                                  </div>
                                </div>
                              </v-tab-item>

                              <!-- Current Voice Tab -->
                              <v-tab-item v-if="currentVoiceFile">
                                <div class="pa-4">
                                  <div class="mb-4">
                                    <h6 class="text-subtitle-1 font-weight-bold mb-2">
                                      <v-icon left class="material-icons-round">audiotrack</v-icon>
                                      Your Uploaded Voice
                                    </h6>
                                    <p class="text-caption mb-3">
                                      This is your current voice file. Admin will process it for your agent.
                                    </p>
                                  </div>

                                  <v-card outlined class="mb-3">
                                    <v-card-text>
                                      <div class="d-flex align-center">
                                        <v-icon class="mr-3 material-icons-round" color="orange">audiotrack</v-icon>
                                        <div class="flex-grow-1">
                                          <p class="text-body-2 mb-1 font-weight-medium">{{ currentVoiceFile.name }}</p>
                                          <p class="text-caption mb-0">
                                            Uploaded: {{ formatDate(currentVoiceFile.uploaded_at) }}
                                            <span v-if="currentVoiceFile.file_size"> • {{ formatFileSize(currentVoiceFile.file_size) }}</span>
                                          </p>
                                        </div>
                                        <v-chip small color="orange" text-color="white">
                                          {{ currentVoiceFile.status || 'Processing' }}
                                        </v-chip>
                                      </div>
                                    </v-card-text>
                                  </v-card>

                                  <div v-if="currentVoiceFile.download_url" class="mb-3">
                                    <p class="text-body-2 mb-2">Preview your voice:</p>
                                    <audio controls :src="currentVoiceFile.download_url" class="mb-3" style="width: 100%;"></audio>
                                  </div>

                                  <div class="d-flex gap-2">
                                    <v-btn
                                      color="orange"
                                      outlined
                                      @click="downloadVoiceFile"
                                      :disabled="!currentVoiceFile.download_url"
                                    >
                                      <v-icon left class="material-icons-round">download</v-icon>
                                      Download
                                    </v-btn>
                                    <v-btn
                                      color="red"
                                      outlined
                                      @click="deleteVoiceFile"
                                      :loading="deleteVoiceLoading"
                                    >
                                      <v-icon left class="material-icons-round">delete</v-icon>
                                      Delete
                                    </v-btn>
                                  </div>
                                </div>
                              </v-tab-item>
                            </v-tabs-items>
                          </v-card>
                        </v-col>
                      </v-row>
                    </div>
                  </v-card-text>
                </v-tab-item>

                <!-- Conversations Tab -->
                <v-tab-item>
                  <v-card-text class="pa-6">
                    <div class="mb-6">
                      <h2 class="text-h4 font-weight-bold mb-2 text-typo">
                        <v-icon left class="material-icons-round" color="orange">phone</v-icon>
                        Voice Agent Conversations
                      </h2>
                      <p class="text-h6 text--secondary">
                        View and manage all conversations from your voice agent calls. Track leads, review transcripts, and analyze call performance.
                      </p>
                    </div>

                    <!-- Conversations Component -->
                    <agent-conversations></agent-conversations>
                  </v-card-text>
                </v-tab-item>
              </v-tabs-items>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
    <vue-footer></vue-footer>

    <!-- Personality Onboarding Dialog -->
    <v-dialog v-model="onboardingDialog" max-width="600px" persistent>
      <PersonalityOnboardingChat 
        @close="onboardingDialog = false"
        @completed="onOnboardingCompleted"
      />
    </v-dialog>

    <!-- Edit Personality Dialog -->
    <v-dialog v-model="editDialog" max-width="600px">
      <v-card>
        <v-card-title>Edit Your AI Personality</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="editedSummary"
            label="Personality Summary"
            rows="8"
            outlined
            hint="Describe how you want your AI assistant to communicate with customers"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editDialog = false">Cancel</v-btn>
          <v-btn color="orange" @click="savePersonality" :loading="saving">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Test Assistant Dialog -->
    <v-dialog v-model="testDialog" max-width="500px">
      <v-card>
        <v-card-title class="orange darken-1 white--text">
          <v-icon left color="white" class="material-icons-round">chat</v-icon>
          Test Your AI Assistant
        </v-card-title>
        <v-card-text class="pa-0">
          <div style="height: 400px; overflow-y: auto; padding: 16px;">
            <div v-for="(message, index) in testMessages" :key="index" class="mb-3">
              <div v-if="message.type === 'ai'" class="d-flex align-start">
                <v-avatar size="32" class="mr-2" color="orange">
                  <v-icon color="white" class="material-icons-round">smart_toy</v-icon>
                </v-avatar>
                <div class="message-bubble ai-message">
                  {{ message.text }}
                </div>
              </div>
              <div v-else class="d-flex align-end justify-end">
                <div class="message-bubble user-message">
                  {{ message.text }}
                </div>
                <v-avatar size="32" class="ml-2">
                  <v-icon color="blue" class="material-icons-round">account</v-icon>
                </v-avatar>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-text-field
            v-model="testInput"
            placeholder="Type a test message..."
            outlined
            dense
            hide-details
            @keyup.enter="sendTestMessage"
            class="mr-2"
          ></v-text-field>
          <v-btn color="orange" @click="sendTestMessage" :disabled="!testInput.trim()" icon>
            <v-icon class="material-icons-round">send</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import PersonalityOnboardingChat from './PersonalityOnboardingChat.vue'
import AgentConversations from './AgentConversations.vue'
import VueHeader from './Layout/Header.vue'
import VueFooter from './Layout/Footer.vue'

export default {
  name: 'AgentSettings',
  components: {
    PersonalityOnboardingChat,
    AgentConversations,
    VueHeader,
    VueFooter
  },
  data() {
    return {
      loading: true,
      personalityData: {
        completed: false,
        approved: false,
        summary: '',
        agent_link: '',
        responses: {}
      },
      onboardingDialog: false,
      editDialog: false,
      editedSummary: '',
      saving: false,
      approving: false,
      resetting: false,
      testDialog: false,
      testMessages: [],
      testInput: '',

      // Tab management
      activeTab: 0,

      // Voice Agent Data
      voiceAgentLoading: false,
      voiceAgent: {
        has_voice_agent: false,
        retell_agent_id: null,
        retell_phone_number: null,
        voice_agent_enabled: false,
        voice_agent_prompt: null,
        voice_agent_created_at: null,
        voice_agent_updated_at: null
      },
      voiceAgentPrompt: '',
      promptLoading: false,
      phoneNumberCopied: false,

      // Voice Recording/Upload
      voiceTab: 0,
      voiceFile: null,
      voiceUploadLoading: false,
      voiceRecordLoading: false,
      uploadedVoiceUrl: null,
      isRecording: false,
      recordedAudio: null,
      recordedAudioBlob: null,
      recordedAudioMimeType: null,
      recordingDuration: 0,
      mediaRecorder: null,
      recordingTimer: null,
      audioChunks: [],
      audioStream: null,
      micTestLoading: false,

      // Current Voice File
      currentVoiceFile: null,
      deleteVoiceLoading: false,

      // Web Call Testing
      webCallLoading: false,
      isWebCallActive: false,
      isAgentSpeaking: false,
      webCallTranscript: [],
      retellWebClient: null,

      // Natural Laughter Enhancement
      laughterUpdateLoading: false
    }
  },
  computed: {
    isLaughterEnhancementEnabled() {
      const enabled = this.voiceAgent && this.voiceAgent.laughter_enhancement_enabled === true
      console.log('🎭 Computed isLaughterEnhancementEnabled:', enabled, 'voiceAgent:', this.voiceAgent)
      return enabled
    }
  },
  watch: {
    'voiceAgent.laughter_enhancement_enabled': {
      handler(newVal, oldVal) {
        console.log('🎭 Watcher: laughter_enhancement_enabled changed from', oldVal, 'to', newVal)
      },
      immediate: true
    }
  },
  mounted() {
    this.loadPersonalityData()
    this.loadVoiceAgentData()
  },

  beforeDestroy() {
    // Clean up any active recording when component is destroyed
    this.cleanupRecording()

    // Clean up any recorded audio URLs
    if (this.recordedAudio) {
      URL.revokeObjectURL(this.recordedAudio)
    }
    if (this.uploadedVoiceUrl) {
      URL.revokeObjectURL(this.uploadedVoiceUrl)
    }

    // Clean up web call
    if (this.retellWebClient && this.isWebCallActive) {
      this.retellWebClient.stopCall()
    }
  },
  methods: {
    async loadPersonalityData() {
      this.loading = true
      try {
        const response = await axios.get('/api/personality/get', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.flag === 1) {
          this.personalityData = response.data.data
        }
      } catch (error) {
        console.error('Error loading personality data:', error)
        this.$toast.error('Error loading personality settings')
      } finally {
        this.loading = false
      }
    },

    startOnboarding() {
      this.onboardingDialog = true
    },

    onOnboardingCompleted() {
      this.onboardingDialog = false
      this.loadPersonalityData()
      this.$toast.success('Personality setup completed successfully!')
    },

    editPersonality() {
      this.editedSummary = this.personalityData.summary
      this.editDialog = true
    },

    async savePersonality() {
      this.saving = true
      try {
        const response = await axios.post('/api/personality/update', {
          summary: this.editedSummary
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.flag === 1) {
          this.personalityData.summary = response.data.data.summary
          this.personalityData.approved = response.data.data.approved
          this.editDialog = false
          this.$toast.success('Personality updated successfully! Please review and approve the changes.')
        }
      } catch (error) {
        console.error('Error updating personality:', error)
        this.$toast.error('Error updating personality')
      } finally {
        this.saving = false
      }
    },

    async approvePersonality() {
      // Show confirmation dialog
      if (!confirm('Are you sure you want to approve this personality? This will activate your AI assistant with the current personality settings.')) {
        return
      }

      this.approving = true
      try {
        const response = await axios.post('/api/personality/approve', {}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.flag === 1) {
          this.personalityData.approved = true
          this.$toast.success('Personality approved! Your AI assistant is now active.')
        }
      } catch (error) {
        console.error('Error approving personality:', error)
        this.$toast.error('Error approving personality')
      } finally {
        this.approving = false
      }
    },

    async resetPersonality() {
      if (!confirm('Are you sure you want to redo your personality setup? This will reset all your current settings.')) {
        return
      }

      this.resetting = true
      try {
        // Reset personality data on server
        await axios.post('/api/personality/reset', {}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        
        this.startOnboarding()
      } catch (error) {
        console.error('Error resetting personality:', error)
        this.$toast.error('Error resetting personality')
      } finally {
        this.resetting = false
      }
    },

    copyLink() {
      navigator.clipboard.writeText(this.personalityData.agent_link)
      this.$toast.success('Link copied to clipboard!')
    },

    testAssistant() {
      this.testMessages = [
        { type: 'ai', text: 'Hi! I\'m your AI assistant. Ask me anything about vehicles or test how I communicate!' }
      ]
      this.testDialog = true
    },

    sendTestMessage() {
      if (!this.testInput.trim()) return

      this.testMessages.push({ type: 'user', text: this.testInput })
      
      // Simulate AI response (in real implementation, this would call the digital assistant API)
      setTimeout(() => {
        this.testMessages.push({ 
          type: 'ai', 
          text: 'This is a test response showing how I would communicate with your personality style!' 
        })
      }, 1000)

      this.testInput = ''
    },

    formatText(text) {
      return text ? text.replace(/\n/g, '<br>') : ''
    },

    // Voice Agent Methods
    async loadVoiceAgentData() {
      this.voiceAgentLoading = true
      try {
        const response = await axios.post('/api/client/voice-agent/get', {}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.flag === 1) {
          this.voiceAgent = response.data.data
          this.voiceAgentPrompt = this.voiceAgent.voice_agent_prompt || ''
          console.log('Voice agent data loaded:', this.voiceAgent)
          console.log('🎭 Laughter enhancement status:', this.voiceAgent.laughter_enhancement_enabled)
          // Load current voice file info
          await this.loadCurrentVoiceFile()
        }
      } catch (error) {
        console.error('Error loading voice agent data:', error)
      } finally {
        this.voiceAgentLoading = false
      }
    },

    async updateVoiceAgentPrompt() {
      this.promptLoading = true
      try {
        const response = await axios.post('/api/client/voice-agent/update', {
          voice_agent_prompt: this.voiceAgentPrompt
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Voice agent instructions updated successfully!')
          await this.loadVoiceAgentData()
        } else {
          this.$toast.error(response.data.message || 'Failed to update instructions')
        }
      } catch (error) {
        console.error('Error updating prompt:', error)
        this.$toast.error('Error updating instructions')
      } finally {
        this.promptLoading = false
      }
    },

    resetPrompt() {
      this.voiceAgentPrompt = this.voiceAgent.voice_agent_prompt || ''
    },

    copyPhoneNumber() {
      if (this.voiceAgent.retell_phone_number) {
        if (navigator.clipboard) {
          navigator.clipboard.writeText(this.voiceAgent.retell_phone_number)
            .then(() => {
              this.phoneNumberCopied = true
              this.$toast.success('Phone number copied to clipboard!')

              setTimeout(() => {
                this.phoneNumberCopied = false
              }, 2000)
            })
            .catch(err => {
              console.error('Failed to copy phone number:', err)
              this.$toast.error('Failed to copy phone number')
            })
        } else {
          // Fallback for older browsers
          const textArea = document.createElement("textarea")
          textArea.value = this.voiceAgent.retell_phone_number
          document.body.appendChild(textArea)
          textArea.select()
          try {
            document.execCommand('copy')
            this.phoneNumberCopied = true
            this.$toast.success('Phone number copied to clipboard!')
            setTimeout(() => {
              this.phoneNumberCopied = false
            }, 2000)
          } catch (err) {
            console.error('Failed to copy phone number:', err)
            this.$toast.error('Failed to copy phone number')
          }
          document.body.removeChild(textArea)
        }
      }
    },

    handleVoiceFileUpload(file) {
      if (file) {
        // Create preview URL
        this.uploadedVoiceUrl = URL.createObjectURL(file)
        this.$toast.info('Voice file loaded. Click "Apply Voice to Agent" to upload.')
      }
    },

    async uploadVoiceToRetell() {
      if (!this.voiceFile) return

      this.voiceUploadLoading = true
      try {
        const formData = new FormData()
        formData.append('voice_file', this.voiceFile)

        const response = await axios.post('/api/client/voice-agent/upload-voice', formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Voice uploaded successfully! Admin will process it for your agent.')
          this.voiceFile = null
          this.uploadedVoiceUrl = null
          await this.loadVoiceAgentData()
        } else {
          this.$toast.error(response.data.message || 'Failed to upload voice')
        }
      } catch (error) {
        console.error('Error uploading voice:', error)
        this.$toast.error('Error uploading voice file')
      } finally {
        this.voiceUploadLoading = false
      }
    },

    async toggleRecording() {
      if (this.isRecording) {
        this.stopRecording()
      } else {
        await this.startRecording()
      }
    },

    async testMicrophone() {
      this.micTestLoading = true
      try {
        // Check if browser supports required APIs
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Your browser does not support microphone access. Please use a modern browser like Chrome, Firefox, or Safari.')
        }

        this.$toast.info('Testing microphone access...')

        // Request microphone access
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true
          }
        })

        // Check if we got a valid stream
        if (!stream || stream.getAudioTracks().length === 0) {
          throw new Error('No audio tracks available. Please check your microphone.')
        }

        // Get microphone info
        const audioTrack = stream.getAudioTracks()[0]
        const settings = audioTrack.getSettings()

        this.$toast.success(`Microphone test successful! Device: ${audioTrack.label || 'Default microphone'}${settings.sampleRate ? ` (${settings.sampleRate}Hz)` : ''}`)

        // Stop the stream immediately
        stream.getTracks().forEach(track => track.stop())

      } catch (error) {
        console.error('Microphone test failed:', error)

        // Handle specific error types
        if (error.name === 'NotAllowedError') {
          this.$toast.error('Microphone access denied. Please allow microphone access in your browser settings and try again.')
        } else if (error.name === 'NotFoundError') {
          this.$toast.error('No microphone found. Please connect a microphone and try again.')
        } else if (error.name === 'NotReadableError') {
          this.$toast.error('Microphone is being used by another application. Please close other apps and try again.')
        } else {
          this.$toast.error('Microphone test failed: ' + error.message)
        }
      } finally {
        this.micTestLoading = false
      }
    },

    async startRecording() {
      try {
        // Check if browser supports required APIs
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Your browser does not support audio recording. Please use a modern browser like Chrome, Firefox, or Safari.')
        }

        if (!window.MediaRecorder) {
          throw new Error('MediaRecorder is not supported in your browser. Please use a modern browser.')
        }

        // Request microphone access with specific constraints
        const constraints = {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100
          }
        }

        this.$toast.info('Requesting microphone access...')

        const stream = await navigator.mediaDevices.getUserMedia(constraints)

        // Check if we got a valid stream
        if (!stream || stream.getAudioTracks().length === 0) {
          throw new Error('No audio tracks available. Please check your microphone.')
        }

        // Store stream reference for cleanup
        this.audioStream = stream

        // Check MediaRecorder support for different formats
        let mimeType = 'audio/webm'
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus'
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4'
        } else if (MediaRecorder.isTypeSupported('audio/wav')) {
          mimeType = 'audio/wav'
        }

        this.mediaRecorder = new MediaRecorder(stream, { mimeType })
        this.audioChunks = []
        this.recordingDuration = 0

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data)
          }
        }

        this.mediaRecorder.onstop = () => {
          try {
            const audioBlob = new Blob(this.audioChunks, { type: mimeType })
            this.recordedAudio = URL.createObjectURL(audioBlob)
            this.recordedAudioBlob = audioBlob // Store the blob for upload
            this.recordedAudioMimeType = mimeType // Store the mime type

            // Stop all tracks to release microphone
            if (this.audioStream) {
              this.audioStream.getTracks().forEach(track => {
                track.stop()
              })
              this.audioStream = null
            }
          } catch (error) {
            console.error('Error processing recorded audio:', error)
            this.$toast.error('Error processing recorded audio')
          }
        }

        this.mediaRecorder.onerror = (event) => {
          console.error('MediaRecorder error:', event.error)
          this.$toast.error('Recording error: ' + event.error.message)
          this.stopRecording()
        }

        this.mediaRecorder.start(1000) // Collect data every second
        this.isRecording = true

        // Start timer
        this.recordingTimer = setInterval(() => {
          this.recordingDuration++

          // Auto-stop after 60 seconds
          if (this.recordingDuration >= 60) {
            this.stopRecording()
            this.$toast.warning('Recording stopped automatically after 60 seconds')
          }
        }, 1000)

        this.$toast.success('Recording started! Speak clearly into your microphone.')

      } catch (error) {
        console.error('Error starting recording:', error)
        this.isRecording = false

        // Handle specific error types
        if (error.name === 'NotAllowedError') {
          this.$toast.error('Microphone access denied. Please allow microphone access and try again.')
        } else if (error.name === 'NotFoundError') {
          this.$toast.error('No microphone found. Please connect a microphone and try again.')
        } else if (error.name === 'NotReadableError') {
          this.$toast.error('Microphone is being used by another application. Please close other apps and try again.')
        } else if (error.name === 'OverconstrainedError') {
          this.$toast.error('Microphone does not meet the required specifications.')
        } else if (error.name === 'SecurityError') {
          this.$toast.error('Microphone access blocked due to security restrictions.')
        } else {
          this.$toast.error('Could not access microphone: ' + error.message)
        }

        // Clean up any partial setup
        this.cleanupRecording()
      }
    },

    stopRecording() {
      try {
        if (this.mediaRecorder && this.isRecording) {
          this.mediaRecorder.stop()
          this.isRecording = false

          if (this.recordingTimer) {
            clearInterval(this.recordingTimer)
            this.recordingTimer = null
          }

          this.$toast.success(`Recording completed (${this.recordingDuration}s)`)
        }
      } catch (error) {
        console.error('Error stopping recording:', error)
        this.$toast.error('Error stopping recording')
        this.cleanupRecording()
      }
    },

    cleanupRecording() {
      try {
        // Stop recording if active
        if (this.mediaRecorder && this.isRecording) {
          this.mediaRecorder.stop()
        }

        // Clear timer
        if (this.recordingTimer) {
          clearInterval(this.recordingTimer)
          this.recordingTimer = null
        }

        // Stop audio stream
        if (this.audioStream) {
          this.audioStream.getTracks().forEach(track => {
            track.stop()
          })
          this.audioStream = null
        }

        // Reset state
        this.isRecording = false
        this.mediaRecorder = null

      } catch (error) {
        console.error('Error during cleanup:', error)
      }
    },

    async uploadRecordedVoiceToRetell() {
      if (!this.recordedAudioBlob) return

      this.voiceRecordLoading = true
      try {
        // Determine file extension based on mime type
        let fileExtension = 'webm'
        let fileName = 'recorded_voice.webm'

        if (this.recordedAudioMimeType) {
          if (this.recordedAudioMimeType.includes('webm')) {
            fileExtension = 'webm'
            fileName = 'recorded_voice.webm'
          } else if (this.recordedAudioMimeType.includes('mp4')) {
            fileExtension = 'mp4'
            fileName = 'recorded_voice.mp4'
          } else if (this.recordedAudioMimeType.includes('wav')) {
            fileExtension = 'wav'
            fileName = 'recorded_voice.wav'
          }
        }

        const formData = new FormData()
        formData.append('voice_file', this.recordedAudioBlob, fileName)

        const uploadResponse = await axios.post('/api/client/voice-agent/upload-voice', formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        })

        if (uploadResponse.data.flag === 1) {
          this.$toast.success('Voice recorded and uploaded successfully! Admin will process it for your agent.')
          this.clearRecording()
          await this.loadVoiceAgentData()
        } else {
          this.$toast.error(uploadResponse.data.message || 'Failed to upload recorded voice')
        }
      } catch (error) {
        console.error('Error uploading recorded voice:', error)
        this.$toast.error('Error uploading recorded voice')
      } finally {
        this.voiceRecordLoading = false
      }
    },

    clearRecording() {
      try {
        // Clean up recorded audio URL
        if (this.recordedAudio) {
          URL.revokeObjectURL(this.recordedAudio)
        }

        // Reset recording state
        this.recordedAudio = null
        this.recordedAudioBlob = null
        this.recordedAudioMimeType = null
        this.recordingDuration = 0
        this.audioChunks = []

        // Clean up any active recording
        this.cleanupRecording()

        this.$toast.info('Recording cleared')
      } catch (error) {
        console.error('Error clearing recording:', error)
      }
    },

    // Current Voice File Methods
    async loadCurrentVoiceFile() {
      try {
        console.log('Loading current voice file...', this.voiceAgent)
        // Check if user has uploaded voice file
        if (this.voiceAgent && this.voiceAgent.voice_file_path) {
          console.log('Voice file path found:', this.voiceAgent.voice_file_path)

          // Get Wasabi S3 URL for the voice file
          let downloadUrl = null
          try {
            const response = await axios.post('/api/client/voice-agent/get-voice-url', {
              voice_file_path: this.voiceAgent.voice_file_path
            }, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            })

            if (response.data.flag === 1) {
              downloadUrl = response.data.data.download_url
            }
          } catch (error) {
            console.error('Error getting voice file URL:', error)
          }

          this.currentVoiceFile = {
            name: this.voiceAgent.voice_file_path.split('/').pop(),
            uploaded_at: this.voiceAgent.voice_agent_updated_at,
            file_size: null, // Will be loaded if needed
            download_url: downloadUrl,
            status: 'Ready for Admin Processing',
            storage: 'Wasabi S3'
          }
          console.log('Current voice file set:', this.currentVoiceFile)
        } else {
          console.log('No voice file path found')
          this.currentVoiceFile = null
        }
      } catch (error) {
        console.error('Error loading current voice file:', error)
        this.currentVoiceFile = null
      }
    },

    async downloadVoiceFile() {
      if (!this.currentVoiceFile) return

      try {
        const response = await axios.post('/api/client/voice-agent/download-voice', {}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          responseType: 'blob' // Important for file download
        })

        // Create blob URL and download
        const blob = new Blob([response.data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = this.currentVoiceFile.name
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.$toast.success('Voice file downloaded successfully!')
      } catch (error) {
        console.error('Error downloading voice file:', error)
        this.$toast.error('Failed to download voice file')
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown'
      try {
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return dateString
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return 'Unknown size'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB'
      return Math.round(bytes / (1024 * 1024) * 100) / 100 + ' MB'
    },

    // Web Call Testing Methods
    async startWebCall() {
      if (!this.voiceAgent.has_voice_agent || !this.voiceAgent.voice_agent_enabled) {
        this.$toast.error('Voice agent is not enabled')
        return
      }

      this.webCallLoading = true
      try {
        // Import Retell Web SDK dynamically
        if (!this.retellWebClient) {
          try {
            const { RetellWebClient } = await import('retell-client-js-sdk')
            this.retellWebClient = new RetellWebClient()
            this.setupWebCallEventListeners()
          } catch (importError) {
            console.error('Failed to import Retell Web SDK:', importError)
            this.$toast.error('Retell Web SDK not available. Please refresh the page and try again.')
            return
          }
        }

        // Call backend to create web call and get access token
        const response = await axios.post('/api/client/voice-agent/create-web-call', {agent_id : this.voiceAgent.retell_agent_id}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.flag === 1) {
          const { access_token } = response.data.data

          // Start the web call
          await this.retellWebClient.startCall({
            accessToken: access_token,
            sampleRate: 24000,
            captureDeviceId: "default",
            playbackDeviceId: "default",
            emitRawAudioSamples: false
          })

          this.isWebCallActive = true
          this.webCallTranscript = []
          this.$toast.success('Web call started! You can now speak with your voice agent.')
        } else {
          this.$toast.error(response.data.message || 'Failed to start web call')
        }
      } catch (error) {
        console.error('Error starting web call:', error)
        if (error.message && error.message.includes('getUserMedia')) {
          this.$toast.error('Microphone access denied. Please allow microphone access and try again.')
        } else {
          this.$toast.error('Failed to start web call. Please check your microphone and try again.')
        }
      } finally {
        this.webCallLoading = false
      }
    },

    stopWebCall() {
      try {
        if (this.retellWebClient && this.isWebCallActive) {
          this.retellWebClient.stopCall()
          this.isWebCallActive = false
          this.isAgentSpeaking = false
          this.$toast.success('Web call ended')
        }
      } catch (error) {
        console.error('Error stopping web call:', error)
        this.$toast.error('Error ending web call')
      }
    },

    setupWebCallEventListeners() {
      if (!this.retellWebClient) return

      this.retellWebClient.on("call_started", () => {
        console.log("Web call started")
        this.isWebCallActive = true
      })

      this.retellWebClient.on("call_ended", () => {
        console.log("Web call ended")
        this.isWebCallActive = false
        this.isAgentSpeaking = false
      })

      this.retellWebClient.on("agent_start_talking", () => {
        console.log("Agent started talking")
        this.isAgentSpeaking = true
      })

      this.retellWebClient.on("agent_stop_talking", () => {
        console.log("Agent stopped talking")
        this.isAgentSpeaking = false
      })

      this.retellWebClient.on("update", (update) => {
        console.log("Web call update:", update)
        if (update.transcript) {
          // Parse transcript and add to our display
          const transcript = update.transcript
          if (transcript.length > 0) {
            // Clear existing transcript and add new messages
            this.webCallTranscript = []
            transcript.forEach(message => {
              this.webCallTranscript.push({
                role: message.role,
                content: message.content
              })
            })
          }
        }
      })

      this.retellWebClient.on("error", (error) => {
        console.error("Web call error:", error)
        this.$toast.error('Web call error: ' + error.message)
        this.stopWebCall()
      })
    },

    /**
     * Update voice agent with natural laughter capabilities
     */
    async updateAgentWithLaughter() {
      this.laughterUpdateLoading = true
      try {
        console.log('🎭 Updating voice agent with natural laughter capabilities...')

        const response = await axios.post('/api/client/voice-agent/update-with-laughter', {}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('🎭 Voice agent updated with natural laughter capabilities! Your AI can now laugh naturally during conversations.')

          console.log('✅ Voice agent laughter update successful:', response.data)

          // Update the local voiceAgent object immediately to hide the button
          this.voiceAgent.laughter_enhancement_enabled = true
          this.voiceAgent.laughter_enhancement_updated_at = new Date().toISOString()

          console.log('🎭 Updated local voiceAgent object:', this.voiceAgent)

          // Force Vue to re-render the component
          this.$forceUpdate()

          // Also refresh voice agent data after a short delay to ensure consistency
          setTimeout(async () => {
            await this.loadVoiceAgentData()
            console.log('🎭 Voice agent after reload:', this.voiceAgent)
          }, 1000)

        } else {
          this.$toast.error('Failed to update voice agent: ' + (response.data.message || 'Unknown error'))
          console.error('❌ Voice agent laughter update failed:', response.data)
        }

      } catch (error) {
        console.error('❌ Error updating voice agent with laughter:', error)

        if (error.response && error.response.data && error.response.data.message) {
          this.$toast.error('Update failed: ' + error.response.data.message)
        } else {
          this.$toast.error('Failed to update voice agent with laughter capabilities. Please try again.')
        }
      } finally {
        this.laughterUpdateLoading = false
      }
    }
  }
}
</script>

<style scoped>
.message-bubble {
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 18px;
  word-wrap: break-word;
}

.ai-message {
  background-color: #f5f5f5;
  color: #333;
}

.user-message {
  background-color: #2196F3;
  color: white;
}

.gap-3 {
  gap: 12px;
}

.card-shadow {
  box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.1) !important;
}

.border-radius-xl {
  border-radius: 1rem !important;
}

.border-radius-lg {
  border-radius: 0.75rem !important;
}

.text-typo {
  color: #344767 !important;
}

.line-height-lg {
  line-height: 1.8 !important;
}

/* Custom button styles */
.v-btn.font-weight-bold {
  font-weight: 700 !important;
}

/* Card hover effects */
.v-card:hover {
  transform: translateY(-2px);
  transition: all 0.3s ease;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .gap-3 {
    gap: 8px;
  }

  .d-flex.flex-wrap {
    flex-direction: column;
  }

  .d-flex.flex-wrap .v-btn {
    width: 100%;
    margin-bottom: 8px;
  }
}
</style>
